/* eslint-disable react/jsx-props-no-spreading */
/*
 * Copyright (c) 2022, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import { Card as MUICard, CardProps as MUICardProps } from '@material-ui/core';
import clsx from 'clsx';
import useStyles from './Card.styles';

export type CardBorderRadius = 'xs' | 'sm' | 'md' | 'lg' | 'square';
export type CardBoxShadow = 'light' | 'dark' | 'none';
export type CardBgColor = 'default' | 'secondary';

interface CardProps extends MUICardProps {
  borderRadius?: CardBorderRadius;
  boxShadow?: CardBoxShadow;
  disabled?: boolean;
  testId: string;
  fullHeight?: boolean;
  bgColor?: CardBgColor;
  width?: any;
}

const Card = (props: CardProps) => {
  const {
    children,
    borderRadius = 'sm',
    boxShadow = 'light',
    disabled = false,
    className,
    variant = 'elevation',
    testId,
    fullHeight = false,
    bgColor = 'default',
    width,
    ...rest
  } = props;
  const classes = useStyles();
  return (
    <MUICard
      {...rest}
      style={{ width }}
      data-cyid={`${testId}-card`}
      variant={variant}
      className={clsx(className, {
        [classes.cardRoot]: true,
        [classes.xsBorderRadius]: borderRadius === 'xs',
        [classes.smBorderRadius]: borderRadius === 'sm',
        [classes.mdBorderRadius]: borderRadius === 'md',
        [classes.lgBorderRadius]: borderRadius === 'lg',
        [classes.square]: borderRadius === 'square',
        [classes.boxShadowNone]: boxShadow === 'none',
        [classes.boxShadowLight]:
          boxShadow === 'light' && variant !== 'outlined',
        [classes.boxShadowDark]: boxShadow === 'dark' && variant !== 'outlined',
        [classes.boxShadowNone]: boxShadow === 'none' && variant !== 'outlined',
        [classes.disabled]: disabled,
        [classes.fullHeight]: fullHeight,
        [classes.colorDefault]: bgColor === 'default',
        [classes.colorSecondary]: bgColor === 'secondary',
      })}
    >
      {children}
    </MUICard>
  );
};

export default Card;
